// Framework and third-party non-ui
import React, { useState } from 'react';

import { useAnimation } from 'framer-motion';

import {
  Container,
  SidebarContainer,
  ToggleContainer,
  SidebarContent,
  CloseContainer,
  SearchContainer,
  ZoomContainer,
  ZoomButtonContainer,
} from './Sidebar-styled';

import CompassVM from '@arcgis/core/widgets/Compass/CompassViewModel';
import ZoomVM from '@arcgis/core/widgets/Zoom/ZoomViewModel';
import LocateVM from '@arcgis/core/widgets/Locate/LocateViewModel.js';
import Search from '@arcgis/core/widgets/Search';

const sidebarStyles = {
  active: {
    x: -320,
  },
  inactive: {
    x: 0,
  },
};

const ToggleButton = ({ isOpen, setIsOpen }) => {
  return (
    <ToggleContainer
      onTap={() => {
        setIsOpen(!isOpen);
      }}
    >
      <div
        aria-hidden="true"
        className="esri-collapse__icon esri-icon-layers"
      ></div>
    </ToggleContainer>
  );
};

const CloseButton = ({ isOpen, setIsOpen }) => {
  return (
    <CloseContainer
      onTap={() => {
        setIsOpen(!isOpen);
      }}
    >
      <div
        style={{ fontSize: '24px' }}
        aria-hidden="true"
        className="esri-icon-collapse"
      ></div>
    </CloseContainer>
  );
};

const Sidebar = ({ width = 320, color = '#fff', children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const controls = useAnimation();

  // Effects
  React.useEffect(() => {
    // play open and close animation when not in partial state
    controls.start(isOpen ? 'active' : 'inactive');
  }, [isOpen, controls]);

  return (
    <Container>
      <ToggleButton isOpen={isOpen} setIsOpen={setIsOpen} />
      <SidebarContainer
        color={color}
        width={width}
        $isopen={isOpen}
        animate={controls}
        variants={sidebarStyles}
        transition={{ type: 'spring', damping: 90, stiffness: 900 }}
      >
        <CloseButton isOpen={isOpen} setIsOpen={setIsOpen} />
        <SidebarContent>{children}</SidebarContent>
      </SidebarContainer>
    </Container>
  );
};

const Button = ({ onTap, type, transform, isActive = true }) => {
  if (!isActive) {
    return <></>;
  }
  return (
    <ToggleContainer onClick={onTap}>
      <div
        style={{ transform: transform }}
        aria-hidden="true"
        className={type}
      ></div>
    </ToggleContainer>
  );
};

const ZoomButton = ({ onTap, type, transform, isActive = true }) => {
  if (!isActive) {
    return <></>;
  }
  return (
    <ZoomButtonContainer onClick={onTap}>
      <div
        style={{ transform: transform }}
        aria-hidden="true"
        className={type}
      ></div>
    </ZoomButtonContainer>
  );
};

const SidebarToggle = ({ onChange, mapView }) => {
  //State

  const [zoom, setZoom] = React.useState(null);
  const [compass, setCompass] = React.useState(null);
  const [locate, setLocate]= React.useState(null)

  const [orientation, setOrientation] = React.useState(0);

  //Actions
  const handleClick = (e) => {
    onChange(e);
  };

  const handleZoomIn = () => {
    zoom.zoomIn();
  };

  const handleZoomOut = () => {
    zoom.zoomOut();
  };

  const handleCompass = () => {
    compass.reset();
  };

  const handleLocate = () => {
    locate.locate()
  }

  // Effects
  React.useEffect(() => {
    if (!mapView) return;

    const search = new Search({
      sources: window.Pin2Flood.SearchConfig.Sources,
      view: mapView,
      container: 'searchWidget',
      enableInfoWindow: false,
      popupEnabled: false,
      locationEnabled: window.Pin2Flood.SearchConfig.LocationEnabled,
      includeDefaultSources:
        window.Pin2Flood.SearchConfig.IncludeDefaultSources,
    });

    const zoomVM = new ZoomVM({
      view: mapView,
    });

    const compassVM = new CompassVM({
      view: mapView,
    });

    const locateVM = new LocateVM({
      view: mapView
    });

    const orientationHandle = mapView.watch('rotation', () => {
      setOrientation(compassVM.orientation.z);
    });

    setZoom(zoomVM);
    setCompass(compassVM);
    setLocate(locateVM);

    return () => {
      if (!mapView) return;
      orientationHandle.remove();
      zoomVM.destroy();
      compassVM.destroy();
      search.destroy();
      locateVM.destroy();
    };
  }, [mapView]);

  return (
    <Container>
      <SearchContainer>
        <div id="searchWidget" />
      </SearchContainer>
      <Button type="esri-icon-layers" onTap={handleClick} />

      {window.innerWidth > 720 && (
        <ZoomContainer>
          <ZoomButton type="esri-icon-plus" onTap={handleZoomIn} />
          <ZoomButton type="esri-icon-minus" onTap={handleZoomOut} />
        </ZoomContainer>
      )}

      <Button
        type="esri-icon-locate"
        onTap={handleLocate}
        isActive={true}
      />

      <Button
        type="esri-icon-compass"
        transform={`rotateZ(${orientation}deg)`}
        onTap={handleCompass}
        isActive={orientation % 360 !== 0}
      />
    </Container>
  );
};

export { Sidebar, SidebarToggle };
